import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, message } from "antd";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { selectBookDemoData, setBookADemoOpen, setBookDemoData, setCurrentStepperStep } from "../slice/contactusSlice";
import CustomCalendar from "../../../shared/components/Calendar/CustomCalendar"
import { selectSelectedDate } from "../../../shared/components/Calendar/slice/calendarSlice";
import { getBookingDemoData, saveBookingDemoData } from "../../../shared/utils/functions/saveBookingDemoData";
import { apiStatuses } from "../../../shared/utils/constantData";
import LazyLoader from "../../../shared/components/LazyLoader/load";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

function AddDateTimeForm() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const selectedDate = useSelector(selectSelectedDate);
    const bookDemoData = useSelector(selectBookDemoData);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(bookDemoData?.selectedEgyptTimeSlot || null);
    const [selectedEgyptTimeSlot, setSelectedEgyptTimeSlot] = useState<string | null>(bookDemoData?.selectedEgyptTimeSlot || null);
    const [isSubmitDemoLoading, setIsSubmitDemoLoading] = useState<boolean>(false);
    const [bookedSlots, setBookedSlots] = useState<string[]>([]);
    const [isLoadingSlots, setIsLoadingSlots] = useState(false);

    useEffect(() => {
        if (!bookDemoData?.selectedEgyptTimeSlot) {
            setSelectedTimeSlot(null)
            setSelectedEgyptTimeSlot(null);
        }
    }, [bookDemoData?.selectedEgyptTimeSlot]);

    useEffect(() => {
        if (selectedDate && !dayjs(selectedDate).isSame(bookDemoData?.selectedDate, 'day')) {
            setSelectedEgyptTimeSlot(null);
            dispatch(setBookDemoData({
                ...bookDemoData,
                selectedDate,
                selectedEgyptTimeSlot: null,
            }));
        }
    }, [selectedDate, bookDemoData, dispatch]);

    useEffect(() => {
        const loadBookingDemoData = async () => {
            setIsLoadingSlots(true);
            const data = await getBookingDemoData();
            setBookedSlots(data?.map((booking: any) => ({
                slot: booking?.SelectedEgyptTimeSlot,
                date: dayjs(booking?.SelectedDate).format('YYYY-MM-DD'),
            })));
            setIsLoadingSlots(false);
        };

        loadBookingDemoData();
    }, []);

    const generateTimeSlots = () => {
        const start = dayjs().hour(9).minute(0).second(0);
        const end = dayjs().hour(16).minute(30).second(0);
        
        const slots: string[] = [];
        let current = start;
        
        while (current.isBefore(end) || current.isSame(end)) {
            slots.push(current.format('hh:mm A'));
            current = current.add(30, 'minute');
        }
        
        return slots;
    };
    
    const isDayBooked = useCallback((date: string) => {
        if (!date) return false;
        const slotsForDate = new Set(
            bookedSlots
                .filter((booking: any) => booking?.date === date)
                .map((booking: any) => booking?.slot)
        );
        const allSlots = generateTimeSlots().map((slot) => slot);
        return allSlots.every((slot) => slotsForDate.has(slot));
    }, [bookedSlots]);

    const fullyBookedDates = useMemo(() => {
        const uniqueDates = Array.from(new Set(bookedSlots.map((booking: any) => booking?.date)));
        return new Set(uniqueDates.filter((date) => isDayBooked(date)));
    }, [bookedSlots, isDayBooked]);
    
    const onTimeSlotSelect = (slot: string) => {
        setSelectedTimeSlot(slot);
        // Convert the selected slot (in user's local time) to Egypt time
        const userTime = dayjs(slot, 'hh:mm A');
        const egyptTime = userTime.tz('Africa/Cairo');
        const formattedSlot = `'${egyptTime.format('hh:mm A')}`;
        setSelectedEgyptTimeSlot(formattedSlot);
        dispatch(setBookDemoData({
            ...bookDemoData,
            selectedDate,
            selectedEgyptTimeSlot: formattedSlot,
        }))
    };

    const handleSubmit = async () => {
        const updatedBookDemoData = {
            ...bookDemoData,
            selectedDate,
            selectedEgyptTimeSlot,
        };
        setIsSubmitDemoLoading(true)
        const res: any = await saveBookingDemoData(updatedBookDemoData);        
        if (res?.status === apiStatuses.success) {
            dispatch(setBookADemoOpen(false))
            dispatch(setCurrentStepperStep(0))
            navigate('/demo-success', { state: { isBookDemoSuccess: true } })
        } else{
            message.error("Something went wrong, please try again later")
        }
        setIsSubmitDemoLoading(false)
    }

    return (
        isLoadingSlots ? (
            <div className='flex flex-col items-center justify-center h-[48rem] sm:h-[42rem]'>
                <LazyLoader />
                <p className='mt-4 text-base font-normal text-indigo-600'>Retrieving time slots..</p>
            </div>
        ) : (
        <>
            <CustomCalendar disabledDates={fullyBookedDates} />
            <div className='mt-1 mb-6 text-center'>
                <h3 className='mb-6 text-lg font-medium text-gray-900'>Time Slot</h3>
                <div className='grid grid-cols-2 gap-5 [@media(min-width:375px)_and_(max-width:767px)]:grid-cols-3 md:grid-cols-5 md:gap-4'>
                    {generateTimeSlots().map((slot) => (
                        <Button
                            onClick={() => onTimeSlotSelect(slot)}
                            className={`${selectedTimeSlot === slot ? 'btn-primary' : 'btn-primary-light'} py-1 px-2`}
                            key={slot}
                            disabled={
                                dayjs(`${selectedDate} ${slot}`, 'YYYY-MM-DD hh:mm A').isBefore(dayjs(), 'minute') ||
                                bookedSlots.some((booking: any) => booking.slot === slot && booking.date === selectedDate)
                            }
                        >
                            {slot}
                        </Button>
                    ))}
                </div>
            </div>
            <Button
                onClick={handleSubmit}
                className='w-full pt-4 pb-10 btn-primary'
                htmlType="submit"
                disabled={!selectedTimeSlot || !selectedDate}
                loading={isSubmitDemoLoading}
            >
                Book Now
            </Button>
        </>
    ))
}

export default AddDateTimeForm